import { API_BASE, request } from "./core";
import { getToken } from "../auth";

export interface TokenResponse {
    access_token: string;
    token_type: string;
}

export interface Customer {
    id: string;
    email: string;
    created_at: string;
    email_verified: boolean;
    first_successful_tool_call_at: string | null;
    mfa_enrollment_deadline_at?: string | null;
}

export function signup(email: string, password: string): Promise<TokenResponse> {
    return request("/auth/signup", {
        method: "POST",
        body: JSON.stringify({ email, password }),
    });
}

export interface LoginResponseWithMFA {
    access_token: string;
    token_type: string;
    mfa_required: boolean;
    mfa_challenge_token: string;
    mfa_enrollment_deadline_at: string | null;
    // True when the customer is a non-admin member of an mfa_required
    // org and hasn't enrolled MFA yet. The caller receives a restricted
    // JWT until MFA enrollment completes and they log in again.
    mfa_enrollment_required: boolean;
}

export function login(email: string, password: string): Promise<LoginResponseWithMFA> {
    return request("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
    });
}

export function loginMfa(
    email: string,
    password: string,
    code: string,
): Promise<TokenResponse> {
    return request("/auth/login/mfa", {
        method: "POST",
        body: JSON.stringify({ email, password, code }),
    });
}

export function finishMfaChallenge(code: string, token: string): Promise<TokenResponse> {
    return request("/auth/login/mfa/challenge", {
        method: "POST",
        tokenOverride: token,
        body: JSON.stringify({ code }),
    });
}

export interface NotificationPreference {
    customer_id: string;
    anomaly_alerts: boolean;
    security_alerts: boolean;
    new_skill: boolean;
    deploy_status: boolean;
    weekly_digest: boolean;
    quota_threshold_alerts?: boolean;
    outbound_webhook_url?: string | null;
}

export interface NotificationPreferenceUpdate {
    anomaly_alerts?: boolean;
    security_alerts?: boolean;
    new_skill?: boolean;
    deploy_status?: boolean;
    weekly_digest?: boolean;
    quota_threshold_alerts?: boolean;
    /** Pass empty string to clear; the backend stores null. */
    outbound_webhook_url?: string | null;
}

export function getNotificationPrefs(): Promise<NotificationPreference> {
    return request("/auth/notifications");
}

export function updateNotificationPrefs(
    body: NotificationPreferenceUpdate,
): Promise<NotificationPreference> {
    return request("/auth/notifications", {
        method: "PATCH",
        body: JSON.stringify(body),
    });
}

export function me(): Promise<Customer> {
    return request("/auth/me");
}

export function verifyEmail(token: string): Promise<{ verified: true }> {
    return request(`/auth/verify-email?token=${encodeURIComponent(token)}`);
}

export function resendVerification(): Promise<void> {
    return request("/auth/resend-verification", { method: "POST" });
}

export function forgotPassword(email: string): Promise<void> {
    return request("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
    });
}

export function resetPassword(token: string, new_password: string): Promise<void> {
    return request("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, new_password }),
    });
}

export interface AccountExportResponse {
    customer: Record<string, unknown>;
    servers: Record<string, unknown>[];
    tool_configs: Record<string, unknown>[];
    audit_logs: Record<string, unknown>[];
    orgs: Record<string, unknown>[];
    audit_next_cursor?: string | null;
}

export async function exportAccountData(): Promise<AccountExportResponse> {
    const token = getToken();
    if (!token) throw new Error("Sign in before exporting your data.");
    const result = await request<AccountExportResponse>("/account/export", { tokenOverride: token });
    const seen = new Set<string>();
    while (result.audit_next_cursor) {
        if (getToken() !== token) throw new Error("Your session changed. Start a new export.");
        const cursor = result.audit_next_cursor;
        if (seen.has(cursor)) throw new Error("Export did not advance. Start a new export.");
        seen.add(cursor);
        const page = await request<AccountExportResponse>(`/account/export?audit_before=${encodeURIComponent(cursor)}`, { tokenOverride: token });
        result.audit_logs.push(...page.audit_logs);
        result.audit_next_cursor = page.audit_next_cursor;
    }
    if (getToken() !== token) throw new Error("Your session changed. Start a new export.");
    return result;
}

export function deleteAccount(): Promise<void> {
    return request("/account/delete", { method: "POST" });
}

export type OauthProvider = "github" | "google";

export function oauthStartUrl(provider: OauthProvider): string {
    return `${API_BASE}/auth/oauth/${provider}/start`;
}

export function listOauthProviders(): Promise<{ providers: OauthProvider[] }> {
    return request("/auth/oauth/providers");
}

export function changePassword(current_password: string, new_password: string): Promise<void> {
    return request("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password, new_password }),
    });
}

export function changeEmail(current_password: string, new_email: string): Promise<Customer> {
    return request("/auth/change-email", {
        method: "POST",
        body: JSON.stringify({ current_password, new_email }),
    });
}
